import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { authAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { 
  FiMail, 
  FiPlus, 
  FiSend,
  FiClock,
  FiCheckCircle,
  FiX,
  FiTrash2
} from 'react-icons/fi';

const AdminInvites = () => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    company_name: ''
  });
  const queryClient = useQueryClient();

  const { data: invites, isLoading } = useQuery(
    'admin-invites',
    () => authAPI.getInvites()
  );

  // Debug: Log the invites data to see what we're getting
  console.log('=== INVITES DEBUG ===');
  console.log('Full invites object:', invites);
  console.log('invites.results:', invites?.results);
  console.log('invites.count:', invites?.count);
  console.log('invites.length:', invites?.length);
  console.log('Type of invites:', typeof invites);
  console.log('Is array:', Array.isArray(invites));
  console.log('Is array results:', Array.isArray(invites?.results));
  console.log('========================');


  const createInviteMutation = useMutation(authAPI.createInvite, {
    onSuccess: (data) => {
      toast.success('Invitation created successfully');
      queryClient.invalidateQueries('admin-invites');
      setShowInviteForm(false);
      setInviteData({ email: '', company_name: '' });
      
      // Show registration URL if available
      if (data.registration_url) {
        console.log('Registration URL:', data.registration_url);
        // You can copy this URL manually if email fails
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to send invitation');
    },
  });

  const resendInviteMutation = useMutation(authAPI.resendInvite, {
    onSuccess: () => {
      toast.success('Invitation resent successfully');
      queryClient.invalidateQueries('admin-invites');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to resend invitation');
    },
  });

  const deleteInviteMutation = useMutation(authAPI.deleteInvite, {
    onSuccess: () => {
      toast.success('Invitation deleted successfully');
      queryClient.invalidateQueries('admin-invites');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete invitation');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inviteData.email || !inviteData.company_name) {
      toast.error('Please fill in all fields');
      return;
    }
    createInviteMutation.mutate(inviteData);
  };

  const handleResend = (inviteId) => {
    if (window.confirm('Are you sure you want to resend this invitation?')) {
      resendInviteMutation.mutate(inviteId);
    }
  };

  const handleDelete = (inviteId) => {
    if (window.confirm('Are you sure you want to delete this invitation? This action cannot be undone.')) {
      deleteInviteMutation.mutate(inviteId);
    }
  };

  const getStatusIcon = (invite) => {
    if (invite.is_used) {
      return <FiCheckCircle className="h-5 w-5 text-green-500" />;
    } else if (invite.is_expired) {
      return <FiX className="h-5 w-5 text-red-500" />;
    } else {
      return <FiClock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusText = (invite) => {
    if (invite.is_used) {
      return 'Used';
    } else if (invite.is_expired) {
      return 'Expired';
    } else {
      return 'Pending';
    }
  };

  const getStatusColor = (invite) => {
    if (invite.is_used) {
      return 'status-completed';
    } else if (invite.is_expired) {
      return 'status-error';
    } else {
      return 'status-pending';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invitations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Send registration invitations to new customers
          </p>
        </div>
        <button
          onClick={() => setShowInviteForm(true)}
          className="btn btn-primary"
        >
          <FiPlus className="h-4 w-4 mr-2" />
          Send Invite
        </button>
      </div>

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Send Invitation
                </h3>
                <button
                  onClick={() => setShowInviteForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData(prev => ({
                      ...prev,
                      email: e.target.value
                    }))}
                    className="form-input"
                    placeholder="customer@company.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={inviteData.company_name}
                    onChange={(e) => setInviteData(prev => ({
                      ...prev,
                      company_name: e.target.value
                    }))}
                    className="form-input"
                    placeholder="Company Name"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowInviteForm(false)}
                    className="btn btn-secondary"
                    disabled={createInviteMutation.isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createInviteMutation.isLoading}
                    className="btn btn-primary"
                  >
                    {createInviteMutation.isLoading ? (
                      <>
                        <div className="spinner mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiSend className="h-4 w-4 mr-2" />
                        Send Invite
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invites List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Sent Invitations
          </h3>
          {(invites?.results || invites) && (invites?.results?.length > 0 || invites?.length > 0) ? (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sent By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sent Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {((invites?.results || invites) || []).map((invite, index) => (
                    <tr key={invite.id || `invite-${index}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FiMail className="h-5 w-5 text-gray-400 mr-2" />
                          <div className="text-sm font-medium text-gray-900">
                            {invite.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invite.company_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(invite)}
                          <span className={`ml-2 status-badge ${getStatusColor(invite)}`}>
                            {getStatusText(invite)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invite.invited_by_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invite.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!invite.is_used && !invite.is_expired && (
                          <button 
                            onClick={() => handleResend(invite.id)}
                            disabled={resendInviteMutation.isLoading}
                            className="text-blue-600 hover:text-blue-900 mr-3 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {resendInviteMutation.isLoading ? 'Sending...' : 'Resend'}
                          </button>
                        )}
                        <button 
                          onClick={() => handleDelete(invite.id)}
                          disabled={deleteInviteMutation.isLoading}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6">
              <FiMail className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invitations sent</h3>
              <p className="mt-1 text-sm text-gray-500">
                Send your first invitation to get started.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInvites;
